import React from "react";
import {
    ApnDetailsResponse,
    FinancePartnerDetailsResponse,
    OpportunityInfoResponse,
} from "../../../../../services/oneButtonServices/opportunityValidationService";
import {Flex, Table} from "antd";
import {areStringsEqual, renderPairItem, renderStatusIcon} from "./OpportunityValidationShared";
import {SalesPersonInfo} from "./SalesPersonInfo";
import {ApnInfo} from "./ApnInfo";
import {FaFileInvoice, FaSalesforce} from "react-icons/fa";

interface OpportunityInfoProps {
    opportunity: OpportunityInfoResponse;
    financePartner: FinancePartnerDetailsResponse;
    apn: ApnDetailsResponse;
}

export const OpportunityInfo: React.FC<OpportunityInfoProps> = ({opportunity, financePartner, apn}) => {

    const ahjStatusIcon = renderStatusIcon(compareAhjs(
        opportunity.sfdc.sfdcPermittingAuthorityAHJTownship,
        opportunity.sfdc.sfdcAHJ));

    const columns = [
        {
            title: (
                <span style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <span>SFDC</span>
                    <FaSalesforce size={22} color="#2563eb" />
                </span>
            ),
            dataIndex: 'items',
            width: '30%',
            render: (value: any) => renderPairItem(value[0]),
        },
        {
            title: financePartner.financePartnerType,
            dataIndex: 'items',
            width: '30%',
            render: (value: any) => renderPairItem(value[1]),
        },
        {
            title: (
                <span style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <span>E-Bill</span>
                    <FaFileInvoice size={18} />
                </span>
            ),
            dataIndex: 'items',
            width: '30%',
            render: (value: any) => renderPairItem(value[2]),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: '10%',
            align: 'center',
        },
    ];

    const items = [
        {
            items: [
                {
                    name: 'Opportunity ID',
                    value: opportunity.sfdc.sfdcOpportunityId,
                },
                {
                    name: 'Lead ID',
                    value: financePartner.financePartnerLeadId,
                },
                null,
            ],
        },
        {
            items: [
                {
                    name: 'Account Name',
                    value: opportunity.sfdc.sfdcAccountName,
                },
                null,
                null,
            ],
        },
        {
            items: [
                {
                    name: 'Direct Lead',
                    value: opportunity.sfdc.sfdcDirectLead,
                },
                null,
                null,
            ],
        },
        {
            items: [
                {
                    name: 'Lead Generator',
                    value: opportunity.sfdc.sfdcLeadGenerator,
                },
                null,
                null,
            ],
        },
        {
            items: [
                {
                    name: 'Lead Generator Prime Closer',
                    value: opportunity.sfdc.sfdcLeadGeneratorPrimeCloser,
                },
                null,
                null,
            ],
        },
        {
            items: [
                {
                    name: 'DL-Assisted By',
                    value: opportunity.sfdc.sfdcDLAssistedBy,
                },
                null,
                null,
            ],
        },
        {
            items: [
                {
                    name: 'Opportunity Name',
                    value: opportunity.sfdc.sfdcOpportunityName,
                },
                {
                    name: 'Name',
                    value: financePartner.financePartnerOpportunityName,
                },
                null,
            ],
            status: renderStatusIcon(areStringsEqual(
                opportunity.sfdc.sfdcOpportunityName,
                financePartner.financePartnerOpportunityName)),
        },
        {
            items: [
                {
                    name: 'Address On Title',
                    value: <div dangerouslySetInnerHTML={{__html: opportunity.sfdc.sfdcAddressOnTitle ?? ''}}/>,
                },
                {
                    name: 'Address',
                    value: financePartner.financePartnerAddress,
                },
                {
                    name: 'Address',
                    value: opportunity.eBill.eBillAddress,
                },
            ],
            status: renderStatusIcon(compareAddresses(
                opportunity.sfdc.sfdcAddressOnTitle,
                financePartner.financePartnerAddress,
                opportunity.eBill.eBillAddress)),
        },
        {
            items: [
                {
                    name: 'Type',
                    value: opportunity.sfdc.sfdcType,
                },
                null,
                null,
            ],
            status: renderStatusIcon(opportunity.sfdc.sfdcType != null),
        },
        {
            items: [
                {
                    name: 'Title',
                    value: opportunity.sfdc.sfdcTitle,
                },
                null,
                null,
            ],
            status: renderStatusIcon(opportunity.sfdc.sfdcTitle != null),
        },
        {
            items: [
                null,
                {
                    name: 'Title Verification Status',
                    value: financePartner.financePartnerTitleVerificationStatus == true ? 'Verified' : 'Not Verified',
                },
                null,
            ],
        },
        {
            items: [
                null,
                {
                    name: 'Credit Status',
                    value: financePartner.financePartnerCreditStatus,
                },
                null,
            ],
            status: renderStatusIcon(financePartner.financePartnerCreditStatus == 'Passed'),
        },
        {
            items: [
                null,
                {
                    name: 'Credit Run Expiration Date',
                    value: financePartner.financePartnerCreditRunExpirationDate,
                },
                null,
            ],
            status: renderStatusIcon(financePartner.financePartnerCreditRunExpirationDate != null),
        },
        {
            items: [
                {
                    name: 'Primary Contact',
                    value: opportunity.sfdc.sfdcPrimaryContact,
                },
                {
                    name: 'Lead Person',
                    value: financePartner.financePartnerLeadPerson,
                },
                null,
            ],
            status: renderStatusIcon(areStringsEqual(
                opportunity.sfdc.sfdcPrimaryContact,
                financePartner.financePartnerLeadPerson))
        },
        ...(financePartner.financePartnerContact && financePartner.financePartnerContact.length > 0 ? 
            financePartner.financePartnerContact.map((contact) => (
                {
                    items: [
                        null,
                        {
                            name: contact.index,
                            value: <div 
                                dangerouslySetInnerHTML={{__html: contact.value ?? ''}}
                                style={{whiteSpace: 'pre-wrap'}}
                            />,
                        }, 
                        null
                    ]
                }))
         : []),
        {
            items: [
                {
                    name: 'Permitting Authority (AHJ - Township)',
                    value: opportunity.sfdc.sfdcPermittingAuthorityAHJTownship,
                },
                null,
                null,
            ],
            status: ahjStatusIcon,
        },
        {
            items: [
                {
                    name: 'AHJ',
                    value: opportunity.sfdc.sfdcAHJ,
                },
                null,
                null,
            ],
            status: ahjStatusIcon,
        },
    ];

    return (
        <Flex vertical>
            <Table
                size={'small'}
                pagination={false}
                // @ts-ignore
                columns={columns}
                dataSource={items}/>

            <SalesPersonInfo
                opportunity={opportunity}
                financePartner={financePartner}/>

            <ApnInfo
                opportunity={opportunity}
                apn={apn}/>
        </Flex>
    );
};

function compareAddresses(sfdcAddress: string | null, financeAddress: string | null, eBillAddress: string | null) {
    if (!sfdcAddress && !financeAddress && !eBillAddress) {
        return false;
    }

    function parseAddress(address: string | null) {
        if (!address) {
            return {
                street: '',
                city: '',
                state: '',
                zip: ''
            };
        }

        let street = '';
        let city = '';
        let state = '';
        let zip = '';

        if (address.includes('<br>')) {
            // Handle sfdcAddress format
            const lines = address.split('<br>');
            street = lines[0]?.trim() || '';
            const cityStateZip = lines[1]?.trim() || '';
            const cityStateZipParts = cityStateZip?.split(' ') || [];
            city = cityStateZipParts.slice(0, -2).join(' ').trim();
            state = cityStateZipParts[cityStateZipParts.length - 2]?.trim().replace(/,$/, '') || ''; // Remove trailing comma
            zip = cityStateZipParts[cityStateZipParts.length - 1]?.trim() || '';
        } else {
            // Handle financeAddress and eBillAddress format
            const parts = address?.split(' ') || [];
            street = parts.slice(0, -3).join(' ').trim();
            city = parts.slice(-3, -2).join(' ').trim();
            state = parts[parts.length - 2]?.trim().replace(/,$/, '') || ''; // Remove trailing comma
            zip = parts[parts.length - 1]?.trim() || '';
        }

        return {street, city, state, zip};
    }

    function normalizeComponent(component: string) {
        return component.trim().toUpperCase().replace(/[^A-Z0-9\s]/g, '');
    }

    function normalizeStreet(street: string) {
        return normalizeComponent(street)?.replace(/\bST\b/g, 'STREET')
            .replace(/\bRD\b/g, 'ROAD')
            .replace(/\bAVE\b/g, 'AVENUE')
            .replace(/\bBLVD\b/g, 'BOULEVARD')
            .replace(/\bDR\b/g, 'DRIVE')
            .replace(/\bLN\b/g, 'LANE')
            .replace(/\bCT\b/g, 'COURT')
            .replace(/\bCRT\b/g, 'COURT')
            .replace(/\bTRL\b/g, 'TRAIL')
            .replace(/\bPK\b/g, 'PARK')
            .replace(/\bCIR\b/g, 'CIRCLE')
            .replace(/\bPL\b/g, 'PLACE')
            .replace(/\bTER\b/g, 'TERRACE')
            .replace(/\bHWY\b/g, 'HIGHWAY')
            .replace(/\bEXPY\b/g, 'EXPRESSWAY')
            .replace(/\bFWY\b/g, 'FREEWAY')
            .replace(/\bSQ\b/g, 'SQUARE')
            .replace(/\bWAY\b/g, 'WAY')
            .replace(/\bPARK\b/g, 'PARKWAY')
            .replace(/\bALY\b/g, 'ALLEY')
            .replace(/\bPKWY\b/g, 'PARKWAY');
    }

    const sfdcParsed = parseAddress(sfdcAddress);
    const financeParsed = parseAddress(financeAddress);
    const eBillParsed = parseAddress(eBillAddress);

    sfdcParsed.street = normalizeStreet(sfdcParsed.street);
    financeParsed.street = normalizeStreet(financeParsed.street);
    eBillParsed.street = normalizeStreet(eBillParsed.street);

    return (
        normalizeComponent(sfdcParsed.street) === normalizeComponent(financeParsed.street) &&
        normalizeComponent(sfdcParsed.street) === normalizeComponent(eBillParsed.street) &&
        normalizeComponent(sfdcParsed.city) === normalizeComponent(financeParsed.city) &&
        normalizeComponent(sfdcParsed.city) === normalizeComponent(eBillParsed.city) &&
        normalizeComponent(sfdcParsed.state) === normalizeComponent(financeParsed.state) &&
        normalizeComponent(sfdcParsed.state) === normalizeComponent(eBillParsed.state) &&
        normalizeComponent(sfdcParsed.zip) === normalizeComponent(financeParsed.zip) &&
        normalizeComponent(sfdcParsed.zip) === normalizeComponent(eBillParsed.zip)
    );
}

function compareAhjs(sfdcPermittingAuthorityAHJTownship: string | null, sfdcAHJ: string | null) {
    const townshipCity = sfdcPermittingAuthorityAHJTownship?.split(',')[0]?.trim();
    const ahjCity = sfdcAHJ?.trim();
    return townshipCity === ahjCity;
}